/**
 * Campaigns API Client
 * 
 * Handles all campaign-related API calls to the backend.
 * Uses Shopify session tokens for authentication.
 */

import { getShopifySessionToken, getCachedHost, getAppBridgeInstance } from "../utils/app-bridge";

// Use empty string for same-origin requests (production) or localhost for dev
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Wait for App Bridge to be initialized (with timeout)
 */
async function waitForAppBridge(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now();
  while (!getAppBridgeInstance()) {
    if (Date.now() - startTime > maxWaitMs) {
      throw new Error("Timeout waiting for App Bridge initialization");
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Wait for App Bridge to be ready before getting token
  await waitForAppBridge();
  
  const sessionToken = await getShopifySessionToken();

  // Get host parameter from cache (persists across navigation)
  const hostParam = getCachedHost();
  
  // Append host parameter to endpoint if present (required in production)
  let fullEndpoint = `${API_BASE_URL}${endpoint}`;
  if (hostParam) {
    const separator = endpoint.includes("?") ? "&" : "?";
    fullEndpoint += `${separator}host=${encodeURIComponent(hostParam)}`;
  }

  const response = await fetch(fullEndpoint, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || error.message || "API request failed");
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ============================================================================
// Types
// ============================================================================

export interface Campaign {
  id: string;
  shopId: string;
  name: string;
  placement: string;
  status: "DRAFT" | "ACTIVE" | "PAUSED" | "ENDED";
  schedule: any;
  targetingRules: any;
  createdAt: string;
  updatedAt: string;
  variants?: CampaignVariant[];
  _count?: {
    sessions: number;
    events: number;
  };
}

export interface CampaignVariant {
  id: string;
  campaignId: string;
  gameId: string;
  gameVersionId: string;
  rewardPolicyId: string;
  weight: number;
  config: any;
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
  updatedAt: string;
  game?: Game;
  gameVersion?: GameVersion;
  rewardPolicy?: RewardPolicy;
}

export interface Game {
  id: string;
  key: string;
  name: string;
  description?: string;
  status: "ACTIVE" | "HIDDEN" | "DEPRECATED";
  tags: string[];
  versions?: GameVersion[];
}

export interface GameVersion {
  id: string;
  gameId: string;
  version: string;
  status: "ACTIVE" | "DEPRECATED";
  manifest: any;
  entryUrl: string;
  assets: any;
  placements: string[];
  configSchema: any;
  releasedAt: string;
}

export interface RewardPolicy {
  id: string;
  shopId: string;
  name: string;
  policy: any;
  expectedValue: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignRequest {
  name: string;
  placement: string;
  status?: "DRAFT" | "ACTIVE" | "PAUSED";
  schedule?: any;
  targetingRules?: any;
}

export interface UpdateCampaignRequest {
  name?: string;
  placement?: string;
  status?: "DRAFT" | "ACTIVE" | "PAUSED" | "ENDED";
  schedule?: any;
  targetingRules?: any;
}

export interface CreateRewardPolicyRequest {
  name: string;
  policy: any;
  expectedValue?: number;
}

export interface UpdateRewardPolicyRequest {
  name?: string;
  policy?: any;
  expectedValue?: number;
}

export interface CreateVariantRequest {
  gameId: string;
  gameVersionId: string;
  rewardPolicyId: string;
  weight?: number;
  config?: any;
  status?: "ACTIVE" | "DISABLED";
}

export interface UpdateVariantRequest {
  weight?: number;
  config?: any;
  status?: "ACTIVE" | "DISABLED";
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get all campaigns for the current shop
 */
export async function getCampaigns(): Promise<Campaign[]> {
  const data = await apiRequest<{ campaigns: Campaign[] }>("/admin/campaigns");
  return data.campaigns;
}

/**
 * Get single campaign by ID
 */
export async function getCampaign(id: string): Promise<Campaign> {
  const data = await apiRequest<{ campaign: Campaign }>(`/admin/campaigns/${id}`);
  return data.campaign;
}

/**
 * Create new campaign
 */
export async function createCampaign(request: CreateCampaignRequest): Promise<Campaign> {
  const data = await apiRequest<{ campaign: Campaign }>("/admin/campaigns", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data.campaign;
}

/**
 * Update existing campaign
 */
export async function updateCampaign(
  id: string,
  request: UpdateCampaignRequest
): Promise<Campaign> {
  const data = await apiRequest<{ campaign: Campaign }>(`/admin/campaigns/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
  return data.campaign;
}

/**
 * Delete campaign (soft delete)
 */
export async function deleteCampaign(id: string): Promise<void> {
  await apiRequest<void>(`/admin/campaigns/${id}`, {
    method: "DELETE",
  });
}

/**
 * Get all available games
 */
export async function getGames(): Promise<Game[]> {
  const data = await apiRequest<{ games: Game[] }>("/admin/games");
  return data.games;
}

/**
 * Get all reward policies for the current shop
 */
export async function getRewardPolicies(): Promise<RewardPolicy[]> {
  const data = await apiRequest<{ policies: RewardPolicy[] }>("/admin/reward-policies");
  return data.policies;
}

/**
 * Create new reward policy
 */
export async function createRewardPolicy(
  request: CreateRewardPolicyRequest
): Promise<RewardPolicy> {
  const data = await apiRequest<{ policy: RewardPolicy }>("/admin/reward-policies", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data.policy;
}

/**
 * Update reward policy
 */
export async function updateRewardPolicy(
  id: string,
  request: UpdateRewardPolicyRequest
): Promise<RewardPolicy> {
  const data = await apiRequest<{ policy: RewardPolicy }>(`/admin/reward-policies/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
  return data.policy;
}

/**
 * Create campaign variant
 */
export async function createVariant(
  campaignId: string,
  request: CreateVariantRequest
): Promise<CampaignVariant> {
  const data = await apiRequest<{ variant: CampaignVariant }>(
    `/admin/campaigns/${campaignId}/variants`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
  return data.variant;
}

/**
 * Update campaign variant
 */
export async function updateVariant(
  campaignId: string,
  variantId: string,
  request: UpdateVariantRequest
): Promise<CampaignVariant> {
  const data = await apiRequest<{ variant: CampaignVariant }>(
    `/admin/campaigns/${campaignId}/variants/${variantId}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
  return data.variant;
}

/**
 * Delete campaign variant
 */
export async function deleteVariant(campaignId: string, variantId: string): Promise<void> {
  await apiRequest<void>(`/admin/campaigns/${campaignId}/variants/${variantId}`, {
    method: "DELETE",
  });
}

// ============================================================================
// Game Preview
// ============================================================================

export interface GamePreviewRequest {
  variantId: string;
  gameKey?: string;
  gameVersion?: string;
  config?: any;
}

export interface GamePreviewResponse {
  manifestUrl: string;
  config: any;
  outcome: {
    outcomeKey: string;
    targetAngle: number;
    totalRotation: number;
    segmentLabel: string;
    segmentColor: string;
    data?: Record<string, unknown>;
  };
}

/**
 * Preview game for a variant
 * 
 * Phase 6.0 Track E: Generate server-authoritative preview outcome
 */
export async function previewGame(request: GamePreviewRequest): Promise<GamePreviewResponse> {
  const data = await apiRequest<GamePreviewResponse>("/admin/games/preview", {
    method: "POST",
    body: JSON.stringify(request),
  });
  return data;
}

// ============================================================================
// Variant Config Persistence
// ============================================================================

export interface UpdateVariantConfigRequest {
  config: any; // Game-specific config (e.g., WheelConfig)
}

export interface UpdateVariantConfigResponse {
  config: any; // Normalized config returned from server
}

/**
 * Update variant game configuration
 * 
 * Phase 7.0: Persist wheel config with validation
 * 
 * @param variantId - Variant ID
 * @param config - Game-specific config object
 * @returns Normalized config from server
 */
export async function updateVariantConfig(
  variantId: string,
  config: any
): Promise<any> {
  const data = await apiRequest<UpdateVariantConfigResponse>(
    `/admin/variants/${variantId}/config`,
    {
      method: "PATCH",
      body: JSON.stringify({ config }),
    }
  );
  return data.config;
}

// ============================================================================
// Targeting Rule Validation
// ============================================================================

export interface TargetingRuleV1 {
  version: "1";
  rule: any;
}

export interface ValidationResult {
  valid: boolean;
  errors: Array<{ path: string; message: string; code: string }>;
}

/**
 * Validate targeting rule via backend API
 * 
 * Phase 5.1: Server-side targeting rule validation
 */
export async function validateTargetingRule(rule: TargetingRuleV1): Promise<ValidationResult> {
  return apiRequest<ValidationResult>("/admin/targeting/validate", {
    method: "POST",
    body: JSON.stringify({ rule }),
  });
}

/**
 * Preview targeting rule evaluation (test against sample data)
 * 
 * Phase 5.1: Preview how many users would match this rule
 */
export async function previewTargetingRule(rule: TargetingRuleV1): Promise<any> {
  return apiRequest<any>("/admin/targeting/preview", {
    method: "POST",
    body: JSON.stringify({ rule }),
  });
}
